import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, Home, ArrowLeft, Send, Check } from "lucide-react";
import { requestAccess } from "../../lib/access";

/**
 * Friendly full-page "you don't have access" screen shown when a user hits a
 * route their Access Matrix grant doesn't include — instead of a silently
 * empty dashboard. Server-side require_access still enforces the API 403; this
 * is the human-facing counterpart. A "Request access" button pings admins.
 */
export default function AccessDenied({ pageId }) {
  const [state, setState] = useState("idle"); // idle | sending | sent | already | error

  const submit = async () => {
    if (!pageId || state === "sending") return;
    setState("sending");
    try {
      const res = await requestAccess(pageId);
      setState(res.already_pending ? "already" : "sent");
    } catch {
      setState("error");
    }
  };

  return (
    <div className="min-h-[70vh] grid place-items-center px-6" data-testid="access-denied">
      <div className="max-w-md w-full text-center fade-up">
        <div className="mx-auto w-16 h-16 rounded-2xl grid place-items-center bg-rose-500/10 border border-rose-500/30 mb-5">
          <ShieldAlert className="w-8 h-8 text-rose-300" />
        </div>
        <h1 className="font-display text-2xl font-bold text-white mb-2">Access restricted</h1>
        <p className="text-slate-400 text-sm leading-relaxed">
          You don’t have permission to open this page. Request access below and an
          administrator will be notified.
        </p>
        {pageId && (
          <p className="mt-3 inline-block text-[11px] font-mono text-slate-500 bg-white/[0.03] border border-white/8 rounded-md px-2.5 py-1">
            {pageId}
          </p>
        )}

        <div className="mt-6">
          {state === "sent" || state === "already" ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" data-testid="access-request-sent">
              <Check className="w-4 h-4" />
              {state === "already" ? "Request already pending" : "Request sent — an admin has been notified"}
            </div>
          ) : (
            <button
              onClick={submit}
              disabled={state === "sending" || !pageId}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-medium transition-colors"
              data-testid="access-request-btn"
            >
              <Send className="w-4 h-4" />
              {state === "sending" ? "Sending…" : "Request access"}
            </button>
          )}
          {state === "error" && (
            <p className="text-xs text-rose-300 mt-2" data-testid="access-request-error">Couldn’t send request. Try again.</p>
          )}
        </div>

        <div className="mt-7 flex items-center justify-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-white/10 text-slate-300 hover:bg-white/5 transition-colors"
            data-testid="access-denied-back"
          >
            <ArrowLeft className="w-4 h-4" /> Go back
          </button>
          <Link
            to="/home"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-white/10 text-slate-300 hover:bg-white/5 transition-colors"
            data-testid="access-denied-home"
          >
            <Home className="w-4 h-4" /> Home
          </Link>
        </div>
      </div>
    </div>
  );
}
